/**
 * invite-utils.ts — Helpers compartilhados para convite/ativação
 *
 * @internal true
 * @owner components/invite
 * @updated 2026-06-19
 */
import { toast } from 'sonner';

/* ────────────────────── TYPES ────────────────────── */

export interface InviteUser {
  id: string;
  nome: string;
  email: string;
  perfil?: string;
  cargo?: string;
  telefone?: string;
  statusConta?: string;
  tokenAtivacao?: string | null;
  codigoDisplay?: string | null;
  tokenExpiraEm?: string | null;
  ativadoEm?: string | null;
  criadoEm?: string | null;
  ativo?: boolean;
  ultimoLogin?: string | null;
  recuperacaoAcesso?: {
    solicitadaEm?: string;
    expiraEm?: string;
    solicitadaPorNome?: string;
    codigo?: string;
    link?: string;
  } | null;
}

export interface InviteResult {
  activationLink: string;
  whatsappLink?: string | null;
  emailSent?: boolean;
  emailReason?: string | null;
  user?: InviteUser;
}

export type InviteStatusType = 'pendente' | 'expirado' | 'ativando' | 'ativo' | 'suspenso';

export interface InviteStatusInfo {
  status: InviteStatusType;
  label: string;
  color: string;
  bgColor: string;
}

export const PERFIL_LABELS: Record<string, string> = {
  ADMIN: 'Administrador',
  ESCOLA: 'Escola',
  PROFESSOR: 'Professor(a)',
  RESPONSAVEL: 'Responsável',
  MUNICIPAL: 'Gestor Municipal',
  ESTADUAL: 'Gestor Estadual',
  REDE: 'Gestor de Rede',
  // Cargos e Perfis Escolares
  DIRETOR: 'Diretor(a)',
  VICE_DIRETOR: 'Vice Diretor',
  'VICE-DIRETOR': 'Vice Diretor',
  COORDENACAO: 'Coordenador(a)',
  COORDENADOR: 'Coordenador(a)',
  SUPERVISOR: 'Supervisor(a)',
  ORIENTADOR: 'Orientador(a)',
  SECRETARIA: 'Secretário(a)',
  SECRETARIO: 'Secretário(a)',
};

/**
 * Normaliza e formata o papel/cargo para exibição nos diálogos e mensagens de convite.
 * Prioriza o cargo funcional específico da escola se fornecido.
 */
export function formatInviteRole(cargo?: string | null, perfil?: string | null): string {
  const raw = (cargo || perfil || '').trim();
  if (!raw) return '';
  const upper = raw.toUpperCase();
  if (PERFIL_LABELS[upper]) return PERFIL_LABELS[upper];
  if (upper === 'DIRETOR(A)' || upper === 'DIRETOR') return 'Diretor(a)';
  if (upper.includes('VICE')) return 'Vice Diretor';
  if (upper.includes('COORDENAD') || upper === 'COORDENACAO' || upper === 'COORDENAÇÃO') return 'Coordenador(a)';
  if (upper.includes('SUPERVISOR')) return 'Supervisor(a)';
  if (upper.includes('ORIENTADOR')) return 'Orientador(a)';
  if (upper.includes('SECRETAR')) return 'Secretário(a)';
  return raw;
}


/* ────────────────────── STATUS ────────────────────── */

export function getInviteStatus(user: InviteUser): InviteStatusInfo {
  // PRIORIDADE MÁXIMA: Desativação / Suspensão da conta
  if (user.ativo === false || user.statusConta === 'SUSPENSA' || user.statusConta === 'BLOQUEADA') {
    return { status: 'suspenso', label: 'Desativado', color: '#ef4444', bgColor: 'rgba(239,68,68,.08)' };
  }
  if (user.statusConta === 'ATIVA') {
    return { status: 'ativo', label: 'Ativo', color: '#10b981', bgColor: 'rgba(16,185,129,.08)' };
  }
  if (user.statusConta === 'ATIVANDO') {
    return { status: 'ativando', label: 'Em ativação', color: '#3b82f6', bgColor: 'rgba(59,130,246,.08)' };
  }
  // PENDENTE
  const { expirado } = getTempoRestante(user.tokenExpiraEm);
  if (expirado || !user.tokenAtivacao) {
    return { status: 'expirado', label: 'Link expirado', color: '#ef4444', bgColor: 'rgba(239,68,68,.08)' };
  }
  return { status: 'pendente', label: 'Aguardando', color: '#f59e0b', bgColor: 'rgba(245,158,11,.08)' };
}

/* ────────────────────── TIME ────────────────────── */

export function getTempoRestante(expiraEm?: string | null): { label: string; expirado: boolean } {
  if (!expiraEm) return { label: '-', expirado: false };
  const diff = new Date(expiraEm).getTime() - Date.now();
  if (diff <= 0) return { label: 'Expirado', expirado: true };
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h >= 24) return { label: `${Math.floor(h / 24)}d ${h % 24}h`, expirado: false };
  return { label: `${h}h ${m}m`, expirado: false };
}

export function formatDate(iso?: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/* ────────────────────── LINKS ────────────────────── */

/**
 * Monta o link de ativação usando window.location.origin para funcionar
 * tanto em localhost:3443 quanto em produção.
 */
export function buildActivationLink(tokenAtivacao: string, codigoDisplay?: string | null): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3443';
  let link = `${origin}/ativar?token=${tokenAtivacao}`;
  if (codigoDisplay) link += `&code=${codigoDisplay}`;
  return link;
}

/**
 * Garante que um link retornado pelo backend use o origin atual do browser
 * (resolve mismatch localhost:3443 vs produção).
 */
export function normalizeActivationLink(rawLink: string): string {
  if (!rawLink) return rawLink;
  try {
    const url = new URL(rawLink);
    const origin = typeof window !== 'undefined' ? window.location.origin : url.origin;
    return `${origin}${url.pathname}${url.search}`;
  } catch {
    return rawLink;
  }
}

/**
 * Monta a URL do WhatsApp com mensagem formatada de convite.
 */
export function buildWhatsAppUrl(
  nome: string,
  activationLink: string,
  perfil?: string,
  telefone?: string,
  cargo?: string,
): string {
  const roleLabel = formatInviteRole(cargo, perfil);
  const cleanLink = normalizeActivationLink(activationLink);

  const lines = [
    `🎓 *Aluno Conectado*`,
    `Olá ${nome.split(' ')[0]}!`,
    roleLabel && roleLabel !== 'Escola' ? `Sua conta de *${roleLabel}* foi criada.` : 'Sua conta foi criada.',
    ``,
    `Use o link abaixo para ativar sua conta e definir sua senha:`,
    cleanLink,
    ``,
    `Em caso de dúvidas, entre em contato com o suporte da escola.`,
  ];

  const msg = encodeURIComponent(lines.join('\n'));

  if (telefone) {
    const cleanPhone = telefone.replace(/\D/g, '');
    const brPhone = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
    return `https://wa.me/${brPhone}?text=${msg}`;
  }
  return `https://wa.me/?text=${msg}`;
}

/* ────────────────────── CLIPBOARD ────────────────────── */

export async function copyActivationLink(
  link: string,
  options?: { silent?: boolean },
): Promise<boolean> {
  try {
    const normalized = normalizeActivationLink(link);
    await navigator.clipboard.writeText(normalized);
    if (!options?.silent) {
      toast.success('Link de ativação copiado!');
    }
    return true;
  } catch {
    if (!options?.silent) {
      toast.error('Erro ao copiar link');
    }
    return false;
  }
}

export async function copyCredentials(email: string, senha?: string, activationLink?: string): Promise<boolean> {
  try {
    let text: string;
    if (activationLink) {
      text = `Link de Ativação: ${normalizeActivationLink(activationLink)}`;
    } else {
      text = `Email: ${email}\nSenha: ${senha || ''}`;
    }
    await navigator.clipboard.writeText(text);
    toast.success('Credenciais copiadas!');
    return true;
  } catch {
    toast.error('Erro ao copiar');
    return false;
  }
}

/* ────────────────────── SENHA ────────────────────── */

/**
 * Gera uma senha alfanumérica aleatória de 10 caracteres.
 * Exclui caracteres ambíguos (0, O, I, l, 1) para legibilidade.
 */
export function gerarSenha(length = 10): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let s = '';
  for (let i = 0; i < length; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
  return s;
}
